// English strings for P5-D (INVENTORY, POS, SHIPPING, MOBILE PWA) (D-013: English only). Owned by that
// worker; spread into `en` as `en.operations`, so keys read `t("operations.…")`. Copy rules:
// DESIGN_SYSTEM §9.6 (plain verbs, sentence case, errors say what failed and how to fix it).
export const operationsStrings = {
  common: {
    rupees: "Rs",
    location: "Location",
    allLocations: "All locations",
    pendingWiring: "Connected in the next update",
    defaultBadge: "Default",
    remove: "Remove",
    add: "Add",
    loadMore: "Load more",
    saved: "Saved",
  },

  errors: {
    insufficientStock: "Not enough stock. Only {available} left of one of the items.",
    insufficientStockGeneric: "Not enough stock for one of the items.",
    belowReserved: "Stock can't go below the units held for open orders.",
    itemUnavailable: "One of the items can't be sold at the counter.",
    tenderShort: "The payment is {due} short of the total.",
    tenderExceeds: "QR and other payments can't be more than the total.",
    addressInvalid:
      "The address doesn't match the Nepal address list. Check the district, municipality and ward.",
    unserviceable: "This store doesn't deliver to that area yet.",
    codIneligible: "COD is off for this area or above its limit.",
    versionMismatch: "Someone else changed this. Reload to see their changes, then try again.",
    duplicateCode: "A location with this code already exists.",
    defaultLocation: "Choose another default location first.",
    defaultProfile: "Make another delivery setup the default before deleting this one.",
  },

  inventory: {
    title: "Inventory",
    lead: "Stock for every product in {store}. Orders, counter sales and adjustments all update it.",
    search: "Search by name, item code or barcode",
    tabAll: "All",
    tabLow: "Low and out of stock",
    caption: "Stock levels",
    product: "Product",
    sku: "Item code",
    available: "Available",
    onHand: "On hand",
    reserved: "Held for orders",
    status: "Status",
    adjust: "Adjust",
    state: {
      in_stock: "In stock",
      low: "Low",
      out_of_stock: "Out of stock",
      untracked: "Not tracked",
    },
    emptyTitle: "No products yet",
    emptyBody: "Add a product and its stock shows up here.",
    noResults: "No products match “{q}”.",
    lowEmptyTitle: "Nothing is running low",
    lowEmptyBody: "Products with 3 or fewer units available show up here.",
    heldHint: "{count} held for orders that aren't paid or packed yet.",
    adjustTitle: "Adjust stock: {item}",
    mode: "What happened?",
    modeAdd: "Received stock",
    modeRemove: "Damaged or lost",
    modeCount: "Counted the shelf",
    quantity: "Quantity",
    countTo: "Units counted",
    adjustHelpAdd: "Adds to what's on hand.",
    adjustHelpRemove: "Takes away from what's on hand.",
    adjustHelpCount: "Sets on hand to exactly this number.",
    adjustSave: "Save adjustment",
    adjusted: "Stock updated. {item} now has {count} on hand.",
    adjustPending: "Stock changes need the stock service, which turns on in the next update.",
    note: "Note",
    // 1026 redesign (owner, 2026-10-22: "same like this" as Collections, Brands and Categories)
    leadShort: "Stock for every product. Orders, counter sales and adjustments all update it.",
    locationsLink: "Locations",
    transfersLink: "Stock transfers",
    filterLabel: "Show",
    showing: { one: "{count} item", other: "{count} items" },
    colItem: "Item",
    colStock: "Stock",
    colWhere: "By location",
    colActions: "Actions",
    held: "{count} held",
    history: "History",
    historyOf: "Stock history: {item}",
    historyEmpty: "No stock changes yet. Adjustments, orders, sales and transfers show up here.",
    historyFailed: "The stock history couldn't be loaded.",
    reasons: {
      sale: "Sold",
      return: "Returned",
      adjust: "Damaged or lost",
      transfer_out: "Sent to another location",
      transfer_in: "Received from a transfer",
      stock_in: "Received stock",
      count: "Shelf count",
      offline_conflict: "Offline sale correction",
    },
    byStaff: "by staff",
    bySystem: "automatic",
    modeLabel: "What happened?",
    quantityLabel: "How many?",
    countLabel: "How many are on the shelf?",
    less: "One less",
    more: "One more",
    locationLabel: "Where",
    preview: "On hand: {from} → {to}",
    previewAt: "On hand at {location}: {from} → {to}",
    belowHeld: "{count} are held for orders, so on hand can't go below that.",
    notePh: "Optional, e.g. supplier invoice number",
    close: "Close",
    cancel: "Cancel",
  },

  locations: {
    title: "Locations and outlets",
    lead: "Where you keep stock: warehouses, shop counters and the online stock pool.",
    caption: "Locations",
    add: "Add location",
    name: "Name",
    code: "Code",
    codeHelp: "Up to 16 letters or numbers, printed on receipts.",
    kind: "Type",
    kinds: {
      warehouse: "Warehouse",
      outlet: "Shop counter",
      online_pool: "Online stock",
    },
    fulfilment: "Ships online orders",
    fulfilmentHelp: "Stock here counts as available on your website.",
    makeDefault: "Make default",
    active: "Active",
    inactive: "Inactive",
    emptyTitle: "No locations yet",
    emptyBody:
      "Your first stock change creates a “Main stock” location. Add shop counters for the POS.",
    created: "{name} added.",
    updated: "{name} updated.",
  },

  delivery: {
    lists: {
      title: "Delivery price lists",
      lead: "What delivery costs, where you deliver and where COD is allowed. Give products their own list when they ship differently.",
      add: "New price list",
      check: "Check a price",
      default: "Default",
      makeDefault: "Make default",
      madeDefault: "Now the default price list",
      edit: "Edit",
      duplicate: "Duplicate",
      copyOf: "{name} (copy)",
      delete: "Delete",
      deleteTitle: "Delete {name}?",
      deleteBody: "Products using it go back to the default price list. This can't be undone.",
      deleted: "Price list deleted",
      defaultNoDelete: "Make another list the default first",
      applyAll: "Use for all products",
      applyTitle: "Use {name} for every product?",
      applyBody:
        "Every product gets this price list, replacing any list it had. New products still use the default.",
      applied: "Applying to {count} products",
      from: "From {fee}",
      byWeight: "By weight",
      districts: "{count} of 77 districts",
      custom: "{count} custom",
      cod: "COD in {count}",
      codNone: "No COD",
      express: "Express",
      pickup: "Pickup",
      freeOver: "Free over {amount}",
      noCodWarning:
        "Cash on delivery is off in every area of {name}. Buyers must pay online, so make sure an online payment method is on.",
      empty: "No price lists yet.",
      menu: "Actions for {name}",
    },
    editor: {
      newTitle: "New price list",
      editTitle: "Edit price list",
      name: "Name",
      namePlaceholder: "e.g. Standard delivery",
      makeDefault: "Default for products without their own list",
      fees: "Default fee",
      feesHelp: "Used in every district without its own fee.",
      byWeight: "Price by weight",
      byWeightHelp: "Six weight steps, like courier rate cards.",
      options: "Options",
      codMax: "COD limit (Rs)",
      codMaxHelp: "Orders above this must be paid online. Empty = no limit.",
      freeOver: "Free delivery over (Rs)",
      freeOverHelp: "Empty = never free.",
      valleyDays: "Days inside the Valley",
      outsideDays: "Days outside",
      express: "Offer express delivery",
      expressExtra: "Extra for express (Rs)",
      pickup: "Offer store pickup (free)",
      districts: "Districts",
      search: "Search districts…",
      filter: {
        all: "All",
        custom: "Own fee",
        off: "Not delivered",
        noCod: "COD off",
      },
      codAllOn: "COD on all",
      codAllOff: "COD off all",
      deliverAll: "Deliver everywhere",
      clearFees: "Clear own fees",
      adjust: "Change prices",
      adjustTitle: "Change prices of the shown districts",
      adjustBy: "By",
      adjustRs: "Rs",
      adjustPct: "%",
      adjustHelp: "Use a minus sign to lower. Applies to the districts shown below ({count}).",
      apply: "Apply",
      undo: "Undo",
      import: "Import",
      export: "Export",
      imported: "Read {matched} districts from the sheet",
      importUnmatched: "Not found: {names}",
      deliverCol: "Deliver",
      codCol: "COD",
      codOff: "Off",
      districtCol: "District",
      deliverRow: "Deliver to {name}",
      codRow: "COD in {name}",
      feeRow: "Fee for {name}, {tier}",
      defaultPlaceholder: "{fee}",
      summary: "Delivers to {deliver} of 77 · {custom} own fees · COD in {cod}",
      checks: {
        zero: "{count} districts deliver for Rs 0",
        noCod: "COD is off everywhere",
        none: "No district is delivered",
      },
      tooMany:
        "Too many different fee combinations. Use the same fee for more districts (at most 50 combinations).",
      kept: "{count} municipality-level areas from before are kept as they are.",
      save: "Save price list",
      saved: "Price list saved",
      cancel: "Cancel",
      close: "Close",
      loading: "Loading districts…",
    },
    calc: {
      title: "Check a delivery price",
      lead: "What checkout charges for an address and parcel.",
      list: "Price list",
      listDefault: "Product's list (default)",
      district: "District",
      area: "Municipality",
      weight: "Weight (kg)",
      subtotal: "Order value (Rs)",
      check: "Check",
      notServed: "Not delivered to this address with this price list.",
      cod: "COD",
      codUpTo: "COD up to {amount}",
      noCod: "No COD",
      days: "{min}–{max} days",
    },
    title: "Delivery",
    lead: "Where {store} delivers, what it costs and where COD is allowed.",
    setupTitle: "Set up delivery",
    setupBody:
      "Pick where you deliver and a starting fee for each area. You can fine-tune areas and weights later.",
    presetLabel: "Where do you deliver?",
    presets: {
      valley_outside: "Kathmandu Valley and the rest of Nepal",
      valley_only: "Kathmandu Valley only",
      all_nepal: "All Nepal, one fee",
    },
    valleyFee: "Fee inside Kathmandu Valley",
    outsideFee: "Fee outside the Valley",
    allNepalFee: "Fee for all Nepal",
    valleyCod: "COD inside the Valley",
    outsideCod: "COD outside the Valley",
    allNepalCod: "COD everywhere",
    createSetup: "Save delivery setup",
    created: "Delivery is set up.",
    zonesTitle: "Delivery areas",
    zonesLead:
      "When an address fits several areas, the most specific one wins: municipality, then district, then the Valley, then province, then all Nepal.",
    profileName: "Setup name",
    zone: "Area",
    areas: "Covers",
    fee: "Fee",
    feeFrom: "From {amount}",
    free: "Free",
    freeOver: "Free over {amount}",
    cod: "COD",
    codOn: "COD on",
    codOff: "COD off",
    codMax: "Up to {amount}",
    eta: "{min}–{max} days",
    etaSame: "{min} days",
    noEta: "No estimate",
    inactiveZone: "Paused",
    editZone: "Edit area",
    addZone: "Add area",
    deleteZone: "Delete area",
    deleteZoneTitle: "Delete {name}?",
    deleteZoneBody:
      "Addresses in this area fall back to the next matching area, or can't be delivered to.",
    zoneSaved: "{name} saved.",
    zoneDeleted: "{name} deleted.",
    zoneName: "Area name",
    zoneAreas: "Places in this area",
    zoneAreasHelp:
      "Search for a province, district or municipality. “All Nepal” and “Kathmandu Valley” are also available.",
    zoneAreasSearch: "Search places",
    zoneAreasEmpty: "Add at least one place.",
    zoneAreasLoading: "Loading the Nepal address list…",
    zoneAreasError: "The address list didn't load. Retry, or keep the current places.",
    days: "Delivery time",
    allNepal: "All Nepal",
    provinceName: "{name} Province",
    placeIn: "{place}, {district}",
    unitWithType: "({type})",
    unitTypes: {
      country: "Country",
      province: "Province",
      geo_zone: "Area",
      district: "District",
      local_level: "Municipality",
    },
    standardFee: "Standard delivery fee",
    expressFee: "Express delivery fee",
    expressHelp: "Leave empty if you don't offer express delivery here.",
    pickupOffered: "Customers can pick up from the store",
    freeOverLabel: "Free delivery over",
    freeOverHelp: "Optional. Standard delivery becomes free when the order reaches this amount.",
    codAllowed: "Allow COD",
    codMaxLabel: "Cash-on-delivery limit",
    codMaxHelp: "Optional. Orders above this amount must be paid online.",
    etaMin: "Delivery takes at least (days)",
    etaMax: "and at most (days)",
    activeZone: "Deliver to this area",
    weightNote: "Weight brackets set through the API are kept: this form edits the main fee.",
    services: {
      standard: "Standard",
      express: "Express",
      pickup: "Pickup",
    },
  },

  pos: {
    title: "Sell",
    close: "Close register view",
    scanLabel: "Scan or search items",
    scanPlaceholder: "Scan a barcode or type a name",
    scanHelp: "Type 3* before a scan to add 3.",
    camera: "Scan with camera",
    cameraTitle: "Point the camera at a barcode",
    cameraUnavailable: "This device can't scan with the camera. Use a scanner or search instead.",
    noMatch: "No product with “{code}”.",
    searchInstead: "Search instead",
    results: "Matching items",
    addItem: "Add {item}",
    left: "{count} left",
    inStock: "In stock: {count}",
    sections: "Counter sections",
    registers: "Registers",
    notTracked: "Not tracked",
    cart: "Current sale",
    cartEmptyTitle: "Scan or add an item first",
    cartEmptyBody: "Items you scan or pick appear here.",
    qty: "Quantity of {item}",
    increase: "Add one {item}",
    decrease: "Remove one {item}",
    removeLine: "Remove {item}",
    clear: "Clear sale",
    subtotal: "Subtotal",
    discount: "Discount",
    total: "Total",
    charge: "Charge {amount}",
    items: { one: "{count} item", other: "{count} items" },
    tenderTitle: "Take payment",
    cash: "Cash received",
    qr: "Paid by QR",
    qrReference: "QR reference",
    qrHelp: "Check the payment on your phone before you confirm. Live QR confirmation comes later.",
    exact: "Exact",
    quick: "Rs {amount}",
    keypad: "Cash keypad",
    key: "Key {key}",
    backspace: "Delete last digit",
    due: "Still due",
    change: "Change to give",
    complete: "Complete sale",
    completing: "Completing…",
    customerPhone: "Customer phone",
    b2bPan: "Buyer tax number, for a tax invoice",
    b2bName: "Buyer business name",
    receiptTitle: "Sale complete",
    receiptNumber: "Receipt {number}",
    orderNumber: "Order {number}",
    giveChange: "Give change {amount}",
    newSale: "New sale",
    offline:
      "You're offline. Counter sales need a connection for now; offline cash sales come later.",
    pendingTitle: "The counter isn't connected yet",
    pendingBody:
      "Selling needs the catalog and order services, which turn on in the next update. Nothing was charged.",
    locationPick: "Selling from",
    installApp: "Install on this device",
  },

  sales: {
    title: "Counter sales",
    lead: "Every sale made at the counter in {store}. Each one is also an order with channel POS.",
    caption: "Counter sales",
    receipt: "Receipt",
    time: "Time",
    order: "Order",
    payment: "Payment",
    total: "Total",
    tenders: {
      cash: "Cash",
      merchant_qr: "QR",
      external: "Other",
    },
    emptyTitle: "No counter sales yet",
    emptyBody: "Sales you make on the Sell screen show up here.",
    openSell: "Open Sell",
  },

  pwa: {
    install: "Install Adyvance",
    installed: "Adyvance is installed on this device.",
  },
} as const;
